import { Document, Packer, Paragraph, TextRun, Table, TableCell, TableRow, WidthType, AlignmentType, HeadingLevel, ImageRun } from 'docx';
import { saveAs } from 'file-saver';
import logoImage from '../assets/logo.png';

const COLORS = {
    kritisch: 'DC2626',
    hoch: 'F59E0B',
    mittel: 'FBBF24',
    niedrig: '10B981',
    primary: 'D4AF37',
};

const STATUS_COLORS = {
    offen: '9CA3AF',
    in_planung: '6366F1',
    in_umsetzung: '3B82F6',
    abgeschlossen: '10B981',
    zurueckgestellt: 'EF4444',
};

const createCoverPage = async (mandantName, currentDate) => {
    const paragraphs = [];

    try {
        const response = await fetch(logoImage);
        const arrayBuffer = await response.arrayBuffer();

        paragraphs.push(
            new Paragraph({
                children: [
                    new ImageRun({
                        data: arrayBuffer,
                        transformation: {
                            width: 200,
                            height: 200,
                        },
                    }),
                ],
                alignment: AlignmentType.CENTER,
                spacing: { after: 600 },
            })
        );
    } catch (error) {
        console.error('Logo-Fehler:', error);
        paragraphs.push(
            new Paragraph({
                children: [
                    new TextRun({
                        text: 'REGENTRA',
                        size: 48,
                        bold: true,
                        color: 'D4AF37',
                    }),
                ],
                alignment: AlignmentType.CENTER,
                spacing: { after: 600 },
            })
        );
    }

    paragraphs.push(
        new Paragraph({
            text: 'GAP-Analyse',
            heading: HeadingLevel.HEADING_1,
            alignment: AlignmentType.CENTER,
            spacing: { before: 400, after: 400 },
        })
    );

    paragraphs.push(
        new Paragraph({
            text: 'Compliance-Lücken gemäß BaFin MaRisk AT 7.2',
            alignment: AlignmentType.CENTER,
            spacing: { after: 1200 },
        })
    );

    paragraphs.push(
        new Paragraph({
            children: [
                new TextRun({
                    text: '─────────────────────────────────',
                    color: 'E5E7EB',
                }),
            ],
            alignment: AlignmentType.CENTER,
            spacing: { after: 400 },
        })
    );

    paragraphs.push(
        new Paragraph({
            children: [
                new TextRun({
                    text: 'Mandant: ',
                    bold: true,
                    size: 24,
                }),
                new TextRun({
                    text: mandantName,
                    size: 24,
                }),
            ],
            alignment: AlignmentType.CENTER,
            spacing: { after: 200 },
        })
    );

    paragraphs.push(
        new Paragraph({
            children: [
                new TextRun({
                    text: 'Erstellt am: ',
                    bold: true,
                    size: 24,
                }),
                new TextRun({
                    text: currentDate,
                    size: 24,
                }),
            ],
            alignment: AlignmentType.CENTER,
            spacing: { after: 400 },
        })
    );

    paragraphs.push(
        new Paragraph({
            children: [
                new TextRun({
                    text: 'Vertraulich',
                    italic: true,
                    size: 20,
                    color: '6B7280',
                }),
            ],
            alignment: AlignmentType.CENTER,
            spacing: { before: 800, after: 400 },
        })
    );

    paragraphs.push(
        new Paragraph({
            text: '',
            pageBreakBefore: true,
        })
    );

    return paragraphs;
};

const createExecutiveSummary = (gaps) => {
    const totalGaps = gaps.length;
    const kritisch = gaps.filter(g => g.kritikalitaet === 'kritisch').length;
    const hoch = gaps.filter(g => g.kritikalitaet === 'hoch').length;
    const mittel = gaps.filter(g => g.kritikalitaet === 'mittel').length;
    const offen = gaps.filter(g => g.massnahme_status === 'offen').length;
    const regulatorisch = gaps.filter(g => g.regulatorische_relevanz).length;

    return [
        new Paragraph({
            text: 'Management Summary',
            heading: HeadingLevel.HEADING_1,
            spacing: { before: 400, after: 200 },
        }),

        new Paragraph({
            text: 'Überblick',
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 200, after: 200 },
        }),

        new Paragraph({
            text: `Diese GAP-Analyse identifiziert ${totalGaps} Compliance-Lücke(n) im Rahmen der BaFin MaRisk AT 7.2 Anforderungen. Die automatische Erkennung erfolgt durch kontinuierliche Überwachung der Schutzbedarfskonformität zwischen Geschäftsprozessen und IT-Anwendungen.`,
            spacing: { after: 400 },
        }),

        createSummaryTable(totalGaps, kritisch, hoch, mittel, offen, regulatorisch),

        new Paragraph({
            text: '',
            pageBreakBefore: true,
        }),
    ];
};

const createSummaryTable = (total, kritisch, hoch, mittel, offen, regulatorisch) => {
    return new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        rows: [
            new TableRow({
                children: [
                    new TableCell({
                        children: [new Paragraph({ text: 'Kennzahl', bold: true })],
                        shading: { fill: 'F3F4F6' },
                    }),
                    new TableCell({
                        children: [new Paragraph({ text: 'Anzahl', bold: true })],
                        shading: { fill: 'F3F4F6' },
                    }),
                ],
            }),
            new TableRow({
                children: [
                    new TableCell({ children: [new Paragraph('Gesamt GAPs')] }),
                    new TableCell({ children: [new Paragraph(total.toString())] }),
                ],
            }),
            new TableRow({
                children: [
                    new TableCell({ children: [new Paragraph('Kritische GAPs')] }),
                    new TableCell({
                        children: [new Paragraph(kritisch.toString())],
                        shading: { fill: kritisch > 0 ? COLORS.kritisch : 'FFFFFF' },
                    }),
                ],
            }),
            new TableRow({
                children: [
                    new TableCell({ children: [new Paragraph('Hohe GAPs')] }),
                    new TableCell({
                        children: [new Paragraph(hoch.toString())],
                        shading: { fill: hoch > 0 ? COLORS.hoch : 'FFFFFF' },
                    }),
                ],
            }),
            new TableRow({
                children: [
                    new TableCell({ children: [new Paragraph('Mittlere GAPs')] }),
                    new TableCell({ children: [new Paragraph(mittel.toString())] }),
                ],
            }),
            new TableRow({
                children: [
                    new TableCell({ children: [new Paragraph('Offene GAPs')] }),
                    new TableCell({ children: [new Paragraph(offen.toString())] }),
                ],
            }),
            new TableRow({
                children: [
                    new TableCell({ children: [new Paragraph('Regulatorisch relevant')] }),
                    new TableCell({ children: [new Paragraph(regulatorisch.toString())] }),
                ],
            }),
        ],
    });
};

const createGapsSection = (gaps, prozesse, anwendungen) => {
    const sections = [
        new Paragraph({
            text: 'Erkannte Compliance-Lücken',
            heading: HeadingLevel.HEADING_1,
            spacing: { before: 400, after: 200 },
        }),
    ];

    if (gaps.length === 0) {
        sections.push(
            new Paragraph({
                text: 'Keine Compliance-Lücken erkannt. Alle Anforderungen sind erfüllt.',
                spacing: { after: 200 },
            })
        );
        return sections;
    }

    // Sortiere nach Kritikalität
    const sortedGaps = [...gaps].sort((a, b) => {
        const order = { kritisch: 1, hoch: 2, mittel: 3, niedrig: 4 };
        return (order[a.kritikalitaet] || 5) - (order[b.kritikalitaet] || 5);
    });

    sortedGaps.forEach((gap, index) => {
        const prozess = prozesse.find(p => p.prozess_id === gap.prozess_id);
        const anwendung = anwendungen.find(a => a.anwendung_id === gap.anwendung_id);

        sections.push(
            new Paragraph({
                text: `${index + 1}. ${gap.gap_titel}`,
                heading: HeadingLevel.HEADING_2,
                spacing: { before: 300, after: 200 },
            })
        );

        if (gap.gap_beschreibung) {
            sections.push(
                new Paragraph({
                    text: gap.gap_beschreibung,
                    spacing: { after: 200 },
                })
            );
        }

        sections.push(createGapMetaTable(gap, prozess, anwendung));

        if (gap.erkennungs_grund) {
            sections.push(
                new Paragraph({
                    text: 'Erkennungsgrund',
                    heading: HeadingLevel.HEADING_3,
                    spacing: { before: 200, after: 100 },
                })
            );
            sections.push(
                new Paragraph({
                    text: gap.erkennungs_grund,
                    spacing: { after: 200 },
                })
            );
        }

        if (gap.massnahme_beschreibung) {
            sections.push(
                new Paragraph({
                    text: 'Maßnahmenbeschreibung',
                    heading: HeadingLevel.HEADING_3,
                    spacing: { before: 200, after: 100 },
                })
            );
            sections.push(
                new Paragraph({
                    text: gap.massnahme_beschreibung,
                    spacing: { after: 200 },
                })
            );
        }

        sections.push(createGapDetailsTable(gap));

        sections.push(
            new Paragraph({
                text: '',
                spacing: { after: 400 },
            })
        );
    });

    return sections;
};

const createGapMetaTable = (gap, prozess, anwendung) => {
    const rows = [];

    if (prozess) {
        rows.push(
            new TableRow({
                children: [
                    new TableCell({
                        children: [new Paragraph({ text: 'Prozess', bold: true })],
                        width: { size: 30, type: WidthType.PERCENTAGE },
                    }),
                    new TableCell({
                        children: [new Paragraph(prozess.prozess_name)],
                        width: { size: 70, type: WidthType.PERCENTAGE },
                    }),
                ],
            })
        );
    }

    if (anwendung) {
        rows.push(
            new TableRow({
                children: [
                    new TableCell({ children: [new Paragraph({ text: 'Anwendung', bold: true })] }),
                    new TableCell({ children: [new Paragraph(anwendung.anwendung_name)] }),
                ],
            })
        );
    }

    rows.push(
        new TableRow({
            children: [
                new TableCell({ children: [new Paragraph({ text: 'Kritikalität', bold: true })] }),
                new TableCell({
                    children: [new Paragraph({ text: gap.kritikalitaet?.toUpperCase() || '-', bold: true })],
                    shading: { fill: COLORS[gap.kritikalitaet] || 'FFFFFF' },
                }),
            ],
        }),
        new TableRow({
            children: [
                new TableCell({ children: [new Paragraph({ text: 'Status', bold: true })] }),
                new TableCell({
                    children: [new Paragraph(gap.massnahme_status?.replace('_', ' ') || '-')],
                    shading: { fill: STATUS_COLORS[gap.massnahme_status] || 'FFFFFF' },
                }),
            ],
        })
    );

    if (gap.frist) {
        rows.push(
            new TableRow({
                children: [
                    new TableCell({ children: [new Paragraph({ text: 'Frist', bold: true })] }),
                    new TableCell({ children: [new Paragraph(new Date(gap.frist).toLocaleDateString('de-DE'))] }),
                ],
            })
        );
    }

    if (gap.verantwortlicher) {
        rows.push(
            new TableRow({
                children: [
                    new TableCell({ children: [new Paragraph({ text: 'Verantwortlicher', bold: true })] }),
                    new TableCell({ children: [new Paragraph(gap.verantwortlicher)] }),
                ],
            })
        );
    }

    return new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        rows: rows,
        margins: {
            top: 100,
            bottom: 100,
            left: 100,
            right: 100,
        },
    });
};

const createGapDetailsTable = (gap) => {
    return new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        rows: [
            new TableRow({
                children: [
                    new TableCell({
                        children: [new Paragraph({ text: 'Kontrollanforderung', bold: true })],
                        shading: { fill: 'F3F4F6' },
                    }),
                    new TableCell({
                        children: [new Paragraph(gap.kontrollanforderung || '-')],
                    }),
                ],
            }),
            new TableRow({
                children: [
                    new TableCell({
                        children: [new Paragraph({ text: 'Ist-Zustand', bold: true })],
                        shading: { fill: 'F3F4F6' },
                    }),
                    new TableCell({
                        children: [new Paragraph(gap.ist_zustand || '-')],
                    }),
                ],
            }),
            new TableRow({
                children: [
                    new TableCell({
                        children: [new Paragraph({ text: 'Soll-Zustand', bold: true })],
                        shading: { fill: 'F3F4F6' },
                    }),
                    new TableCell({
                        children: [new Paragraph(gap.soll_zustand || '-')],
                    }),
                ],
            }),
            new TableRow({
                children: [
                    new TableCell({
                        children: [new Paragraph({ text: 'Regulatorisch relevant', bold: true })],
                        shading: { fill: 'F3F4F6' },
                    }),
                    new TableCell({
                        children: [new Paragraph(gap.regulatorische_relevanz ? 'Ja' : 'Nein')],
                    }),
                ],
            }),
        ],
    });
};

export const exportGapAnalyseToWord = async (data) => {
    const { gaps, prozesse, anwendungen, mandantName } = data;
    const currentDate = new Date().toLocaleDateString('de-DE', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });

    const coverPage = await createCoverPage(mandantName, currentDate);

    const sections = [
        ...coverPage,
        ...createExecutiveSummary(gaps),
        ...createGapsSection(gaps, prozesse, anwendungen),
    ];

    const doc = new Document({
        creator: 'Regentra',
        title: `GAP-Analyse - ${mandantName}`,
        description: 'Compliance-Lücken gemäß BaFin MaRisk AT 7.2',
        sections: [
            {
                properties: {},
                children: sections,
            },
        ],
    });

    const blob = await Packer.toBlob(doc);
    const fileName = `GAP-Analyse_${mandantName}_${new Date().toISOString().split('T')[0]}.docx`;
    saveAs(blob, fileName);
};